import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilRefresher_UNSTABLE, useRecoilState } from 'recoil';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { clearAccessTokens, saveAccessTokens } from '@ecp/common/src/utils/utils';
import { FlexBox, FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import { TextBox } from '@ecp/common/src/text/TextBox';
import Dropdown from '@ecp/common/src/components/dropdown/bo/Dropdown';
import Popup from '@ecp/common/src/components/modal/bo/Popup';
import { ReactComponent as InfoIcon } from '@ecp/common/src/assets/icon/icon__tooltip-info.svg';

import { userInfoState } from '@bo-recoil/common/user/atom';
import { menusState } from '@bo-recoil/common/atom';
import { useAlertDialog } from '@bo-hooks/common/useModal';
import authApi from '@bo-apis/common/authApi';
import CommonApi from '@bo-apis/common/commonApi';

const SelectJobGroupPopup = ({ isNeedSelect, open, onConfirm, onClose, ssoUserInfo }) => {
  const navigate = useNavigate();
  const [memberInfo, setMemberInfo] = useRecoilState(userInfoState);
  const [jobGroups, setJobGroups] = useState([]);
  const refreshMenuState = useRecoilRefresher_UNSTABLE(menusState);
  const [selectJobGroup, setSelectJobGroup] = useState(null);
  const { showAlertDialog } = useAlertDialog();

  useEffect(() => {
    if (open) {
      (async () => {
        const { result } = await CommonApi.getJobGroups(ssoUserInfo);
        if (result) {
          const jobGroups = result.map(({ jobGrpNo, jobGrpNm }) => {
            return {
              key: jobGrpNo,
              label: jobGrpNm,
              value: jobGrpNo,
            };
          });

          setJobGroups(jobGroups);
        }
      })();
    }
  }, [open, ssoUserInfo, setJobGroups]);

  useEffect(() => {
    if (memberInfo?.jobGroupNumber && jobGroups?.length > 0) {
      const jobGroup = jobGroups.find((jobGroup) => jobGroup.value === memberInfo?.jobGroupNumber);
      setSelectJobGroup(jobGroup);
    }
  }, [memberInfo, jobGroups]);

  const handleChangeJobGroup = useCallback((item) => {
    setSelectJobGroup(item);
  }, []);

  const handleOnClickConfirm = useCallback(async () => {
    if (!selectJobGroup) {
      showAlertDialog('권한을 선택해주세요.');
      return;
    }

    const result = await CommonApi.changeJobGroup({ ...ssoUserInfo, jobGroupNo: selectJobGroup.value });
    clearAccessTokens();
    if (result) {
      const {
        data: { accessToken, refreshToken },
      } = await authApi.ssoLogin(ssoUserInfo);
      saveAccessTokens(accessToken, refreshToken);
      authApi.info().then(({ result }) => {
        setMemberInfo(result);
        refreshMenuState();
        navigate('/');
      });
    } else {
      showAlertDialog('실패했습니다.');
    }
    onConfirm?.();
  }, [ssoUserInfo, navigate, onConfirm, refreshMenuState, selectJobGroup, setMemberInfo, showAlertDialog]);

  const handleOnClickClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const labelText = useMemo(() => (isNeedSelect ? '권한선택' : '권한변경'), [isNeedSelect]);

  return (
    <Popup
      type={'Small'}
      header={labelText}
      onClose={handleOnClickClose}
      onCancelClick={handleOnClickClose}
      onConfirmClick={handleOnClickConfirm}
      useCancelButton={false}
      useConfirmButton={true}
      closable={!isNeedSelect}
      open={open}
    >
      <FlexBoxColumn width={'100%'}>
        <Row>
          <FlexBox width={'120px'} height={'100%'} background-color={'#F2F3F8'}>
            <Spacing left={14} top={13.5} bottom={13.5}>
              <TextBox size={13} weight={700}>
                {labelText}
              </TextBox>
            </Spacing>
          </FlexBox>
          <Spacing left={14} />
          <Dropdown
            width={'180px'}
            items={jobGroups}
            onChange={handleChangeJobGroup}
            defaultValue={{ label: '', key: '', value: '' }}
            value={selectJobGroup ? selectJobGroup : jobGroups[0]}
          />
        </Row>
        <Spacing top={6} />
        <FlexBox width={'100%'}>
          <InfoIcon width={'18px'} height={'18px'} />
          <Spacing left={6} />
          <TextBox size={13}>다음 로그인 시 선택한 권한으로 자동 로그인 됩니다.</TextBox>
        </FlexBox>
      </FlexBoxColumn>
    </Popup>
  );
};

const Row = styled(FlexBox)`
  width: 100%;
  height: 44px;
  border-top: 1px solid #e9eaf0;
  border-bottom: 1px solid #e9eaf0;
`;

SelectJobGroupPopup.propTypes = {
  isNeedSelect: PropTypes.bool,
  open: PropTypes.bool,
  onConfirm: PropTypes.func,
  onClose: PropTypes.func,
  ssoUserInfo: PropTypes.object,
};

export default SelectJobGroupPopup;
